/**
 * Exporting the roster as a PDF: draw both grids to images, then lay them out
 * (with the manning stats) on A4 landscape pages.
 */

import type { HistorySnapshot, RosterGridData, RosterState } from "@/types/api";
import { getModeConfig, getStartHour } from "@/config/modeConfig";
import { RosterCanvas } from "@/canvas/RosterCanvas";
import { buildOfficerGridDataForExport } from "@/components/tabs/home/gridData";

export interface ExportData {
  counterImg: string; counterW: number; counterH: number;
  officerImg: string; officerW: number; officerH: number;
  stats: string; ts: string;
}

/** Draw the counter grid and the officer schedule off-screen at print size. */
export function renderExportImages(
  gridData: RosterGridData,
  state: RosterState,
  snapshot: HistorySnapshot,
): ExportData {
  const modeConfig = getModeConfig(state.operation_mode);
  const printW = 1400;

  const counterCanvas = document.createElement('canvas');
  const counterRenderer = new RosterCanvas(counterCanvas, {
    cellWidth: 20, cellHeight: 20, labelWidth: 70, labelHeight: 25,
    fontSize: 8, mergeConsecutive: true,
    zoneBoundaryRows: modeConfig.zoneBoundaryRows,
    textMode: 'officer_meta',
  });
  counterRenderer.render(gridData);
  counterRenderer.resizeToFit(printW, gridData.num_counters * 20 + 30);
  const counterImg = counterCanvas.toDataURL('image/png', 1.0);
  const counterW = counterCanvas.width;
  const counterH = counterCanvas.height;
  counterRenderer.destroy();

  const startHour = getStartHour(state.shift_type ?? 'morning');
  const officerGridData = buildOfficerGridDataForExport(
    snapshot.officer_schedules,
    state.officer_metadata,
    gridData.time_labels,
    48,
    state.operation_mode,
    startHour,
    snapshot.duty_slots,
  );
  const officerCanvas = document.createElement('canvas');
  const officerRenderer = new RosterCanvas(officerCanvas, {
    cellWidth: 22, cellHeight: 20, labelWidth: 140, labelHeight: 22,
    fontSize: 8, mergeConsecutive: true, textMode: 'cell_text',
  });
  officerRenderer.render(officerGridData);
  officerRenderer.resizeToFit(printW, officerGridData.num_counters * 20 + 25);
  const officerImg = officerCanvas.toDataURL('image/png', 1.0);
  const officerW = officerCanvas.width;
  const officerH = officerCanvas.height;
  officerRenderer.destroy();

  return {
    counterImg, counterW, counterH,
    officerImg, officerW, officerH,
    stats: snapshot.stats ?? '',
    ts: snapshot.timestamp ?? '',
  };
}

/** Build the PDF from rendered images and hand it to the browser to save. */
export async function writeRosterPdf(
  data: ExportData,
  sessionId: string,
  operationMode: string | undefined,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const margin = 6;
  const contentW = 285; // A4 landscape usable width: 297 - 2*6 mm
  const pageH = 210;
  const titleY = margin + 5;
  const imgY = titleY + 5;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Every page names the session it came from: a printed roster passed around
  // an ops room is otherwise unattributable, and the session ID is the only
  // route back to the live roster it was exported from.
  const sessionLine = sessionId ? `Session ${sessionId}` : '';
  const pageHeading = (title: string) => {
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);
    pdf.text(title, margin, titleY);
    if (sessionLine) pdf.text(sessionLine, margin + contentW, titleY, { align: 'right' });
  };

  // Add an image, splitting it across pages if it's taller than the available space.
  const addImgSplit = async (imgData: string, natW: number, natH: number, y0: number) => {
    const mmPerPx = contentW / natW;
    const totalMM = natH * mmPerPx;
    const firstAvail = pageH - y0 - margin;

    if (totalMM <= firstAvail) {
      pdf.addImage(imgData, 'PNG', margin, y0, contentW, totalMM);
      return;
    }

    const img = new Image();
    img.src = imgData;
    await new Promise<void>(r2 => { img.onload = () => r2(); });

    let srcYPx = 0;
    let isFirst = true;
    while (srcYPx < natH) {
      const avail = isFirst ? firstAvail : (pageH - 2 * margin);
      const slicePx = Math.min(Math.round(avail / mmPerPx), natH - srcYPx);
      if (slicePx <= 0) break;
      const tmp = document.createElement('canvas');
      tmp.width = natW;
      tmp.height = slicePx;
      const ctx = tmp.getContext('2d');
      if (ctx) ctx.drawImage(img, 0, srcYPx, natW, slicePx, 0, 0, natW, slicePx);
      const sliceH = slicePx * mmPerPx;
      pdf.addImage(tmp.toDataURL('image/png'), 'PNG', margin, isFirst ? y0 : margin, contentW, sliceH);
      srcYPx += slicePx;
      if (srcYPx < natH) pdf.addPage();
      isFirst = false;
    }
  };

  // Page 1 — Counter Timetable
  pageHeading('Counter Timetable');
  await addImgSplit(data.counterImg, data.counterW, data.counterH, imgY);

  // Page 2 — Manning Stats
  pdf.addPage();
  pageHeading('Manning Stats');
  if (data.stats) {
    pdf.setFontSize(6.5);
    pdf.setTextColor(55, 65, 81);
    let y = imgY + 2;
    for (const line of data.stats.split('\n')) {
      if (y > pageH - margin) { pdf.addPage(); y = margin + 5; }
      pdf.text(line, margin, y);
      y += 3.2;
    }
  }

  // Page 3 — Officer Schedule
  pdf.addPage();
  pageHeading('Officer Schedule');
  await addImgSplit(data.officerImg, data.officerW, data.officerH, imgY);

  // "<ACar|DCar>_<session id>" — the two things that identify a roster to the
  // people who file these, so a saved PDF needs no renaming. Falls back to the
  // version timestamp only when there is no session ID to name it by.
  const modeLabel = operationMode === 'departure' ? 'DCar' : 'ACar';
  const fileStem = sessionId
    ? `${modeLabel}_${sessionId}`
    : `${modeLabel}${data.ts ? '_' + data.ts.replace(/[: /]/g, '-') : ''}`;
  pdf.save(`${fileStem}.pdf`);
}
